import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Checkbox } from './ui';
import { acceptAll, getConsent, hasDecided, rejectAll, setConsent } from '../lib/consent';

/**
 * Consent notice.
 *
 * Placed at the foot of the viewport rather than as a blocking overlay: an
 * interstitial on a paid landing page costs conversions and is not required —
 * what is required is that nothing non-essential runs before a real choice is
 * made, which `lib/consent.ts` enforces regardless of what this banner looks like.
 *
 * Accept and reject are given equal visual weight, which is both the fair
 * presentation and the one regulators expect.
 */
const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [customising, setCustomising] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [advertising, setAdvertising] = useState(true);

  useEffect(() => {
    // Deferred a beat so the banner never competes with first paint (or LCP).
    const timer = window.setTimeout(() => setVisible(!hasDecided()), 800);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const stored = getConsent();
    if (stored.decidedAt) {
      setAnalytics(stored.analytics);
      setAdvertising(stored.advertising);
    }
  }, []);

  const bannerRef = useRef<HTMLDivElement>(null);

  /**
   * Reserve the space the banner occupies.
   *
   * Being `fixed` it is outside flow, so on a short viewport it simply sat on
   * top of whatever was at the bottom of the page — on a 320px phone that was
   * the email field, the password field and the Login button, and clicks landed
   * on the banner instead of the form. Shrinking the banner alone does not fix
   * that: any fixed footer of any height hides content underneath it. Padding
   * the body by the measured height means the page can always scroll clear of
   * it, at every viewport, whatever the banner ends up containing.
   */
  useEffect(() => {
    if (!visible) return;

    const apply = () => {
      const height = bannerRef.current?.offsetHeight ?? 0;
      document.body.style.paddingBottom = `${height}px`;
      /**
       * Padding the body cannot move another *fixed* footer, and there is one:
       * the sticky enrol bar on a course page sits at `bottom-0` too, under
       * this banner, at a lower z-index. On a phone that made the pinned Enrol
       * button unclickable for every first-time visitor — which is every click
       * an advertisement buys. Publishing the measured height lets anything
       * else pinned to the bottom stand on top of this rather than beneath it.
       */
      document.documentElement.style.setProperty('--cookie-banner-height', `${height}px`);
    };

    apply();
    const observer = new ResizeObserver(apply);
    if (bannerRef.current) observer.observe(bannerRef.current);
    window.addEventListener('resize', apply);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', apply);
      document.body.style.paddingBottom = '';
      document.documentElement.style.removeProperty('--cookie-banner-height');
    };
  }, [visible, customising]);

  if (!visible) return null;

  const close = () => setVisible(false);

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-label="Cookie preferences"
      /**
       * The height ceiling matters as much as the layout.
       *
       * On a 320px phone this banner rendered 303px tall — 53% of the viewport —
       * because the three action buttons each wrapped onto their own line under a
       * five-line paragraph. It sat opaquely over the email field, the password
       * field, the submit button and the terms checkbox on /auth, so a first-time
       * visitor on a small phone could not complete signup at all: clicks landed
       * on the banner instead of the form.
       */
      className="fixed inset-x-0 bottom-0 z-toast max-h-[45svh] overflow-y-auto border-t border-border bg-background-card p-4 shadow-lg sm:p-5"
    >
      <div className="container-editorial flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-2xl">
          <p className="text-caption text-text-secondary">
            We use cookies to keep you signed in and, with your permission, to understand how the site is used and to
            measure our advertising. You can change your mind at any time.{' '}
            <Link to="/privacy-policy" className="font-medium text-primary underline-offset-4 hover:underline">
              Privacy policy
            </Link>
          </p>

          {customising && (
            <div className="mt-4 space-y-3 border-t border-border pt-4">
              <Checkbox
                label="Essential"
                hint="Sign-in, your cart and security. Always on."
                checked
                disabled
                readOnly
              />
              <Checkbox
                label="Analytics"
                hint="How pages and courses are used, so we can improve them."
                checked={analytics}
                onChange={(event) => setAnalytics(event.target.checked)}
              />
              <Checkbox
                label="Advertising"
                hint="Measuring which campaigns bring people here."
                checked={advertising}
                onChange={(event) => setAdvertising(event.target.checked)}
              />
            </div>
          )}
        </div>

        {/* Two columns on the narrowest screens keeps the actions to two rows
            instead of three; from `sm` up the original inline row is restored. */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-2.5 lg:shrink-0">
          {customising ? (
            <Button
              size="sm"
              onClick={() => {
                setConsent({ analytics, advertising });
                close();
              }}
            >
              Save choices
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setCustomising(true)}>
              Choose
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              rejectAll();
              close();
            }}
          >
            Reject non-essential
          </Button>
          <Button
            size="sm"
            onClick={() => {
              acceptAll();
              close();
            }}
          >
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
